import { ECardinality, EProjectType, type IActionDefinitionDto, type IActionParameter } from "@kuika/kuika-cl-model";
import React from "react";
import { getActionReturnTypeIconName, isVoidReturnType } from "../../../../../shared/utility/action-definition-summary";
import {
  getActionDocDetails,
  getActionDocSummary,
  getActionDocUrl,
  getActionParameterDoc
} from "../../../../../shared/utility/action-doc";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { getUserFacingActionParameters, type IUserFacingActionParameter } from "../action-parameter-surface";
import { ActionDocGlyph, ActionDocTooltip } from "./action-doc-hint";
import {
  EActionNodeCategory,
  EActionPickerNodeKind,
  getNodeChain,
  toActionPickerTestIdSlug,
  type IActionPickerNode
} from "./action-picker-node";

export interface IActionPickerPreviewProps {
  node: IActionPickerNode | null;
  definition: IActionDefinitionDto | undefined;
  /** The event the action will be added to, when the host has events ("On Click"). */
  eventLabel: string | undefined;
  /** Why adding this action will stop short — e.g. a domain it needs is not enabled. */
  lockReason: string | undefined;
  /** The host project's type — some parameters are drawn or offered only on some project types. */
  projectType?: EProjectType;
  onAdd: (node: IActionPickerNode) => void;
}

/** The path between the event and the action itself: "Custom › Managed DB › Orders". */
function getContextTrail(node: IActionPickerNode): string {
  return getNodeChain(node)
    .slice(0, -1)
    .filter((link: IActionPickerNode): boolean => link.kind !== EActionPickerNodeKind.event)
    .map((link: IActionPickerNode): string => link.label)
    .join(" › ");
}

interface IPreviewSectionProps {
  title: string;
  children: React.ReactNode;
}

const PreviewSection = ({ title, children }: IPreviewSectionProps): JSX.Element => (
  <section className="kuika-action-picker-popup__preview-section">
    <h4 className="kuika-action-picker-popup__preview-heading">{title}</h4>
    {children}
  </section>
);

interface IParameterListProps {
  definition: IActionDefinitionDto;
  projectType: EProjectType | undefined;
}

interface IParameterRowProps {
  entry: IUserFacingActionParameter;
  doc: string | undefined;
}

const ParameterRow = ({ entry, doc }: IParameterRowProps): JSX.Element => {
  const parameter: IActionParameter = entry.parameter;
  const label: string = entry.label;
  const typeLabel: string = parameter.isEnum ? "enum" : String(parameter.Typename ?? "");
  const cardinalitySuffix: string = parameter.cardinality === ECardinality.Many ? "[]" : "";
  // The name takes what is left and slides on the row's hover when it does not fit; the doc glyph and
  // the type sit at the right edge, the glyph first, and never shrink.
  const row: JSX.Element = (
    <li
      className={`kuika-action-picker-popup__param ${OVERFLOW_REVEAL_HOST_CLASS}`}
      tabIndex={doc ? 0 : undefined}
      data-testid={`actionPickerParam-${toActionPickerTestIdSlug(parameter.name)}`}
    >
      <KuikaOverflowText className="kuika-action-picker-popup__param-name" title={parameter.name}>
        {label}
        {entry.isAddable && <span className="kuika-action-picker-popup__preview-muted"> · optional</span>}
      </KuikaOverflowText>
      <span className="kuika-action-picker-popup__param-meta">
        {doc && <ActionDocGlyph />}
        <span className="kuika-action-picker-popup__param-type">
          {typeLabel}
          {cardinalitySuffix}
        </span>
      </span>
    </li>
  );
  if (!doc) {
    return row;
  }
  // The whole row is the trigger: the glyph is a 12px cue, the row is what the pointer rests on.
  return (
    <ActionDocTooltip content={doc} contentTestId={`actionPickerParamDoc-${toActionPickerTestIdSlug(parameter.name)}`}>
      {row}
    </ActionDocTooltip>
  );
};

/**
 * Exactly the parameters the action's actions-panel editor lets the user set, under the labels it
 * draws — including a special symbols group's own subset — plus the optionals the menu can add.
 */
const ParameterList = ({ definition, projectType }: IParameterListProps): JSX.Element => {
  const parameters: IUserFacingActionParameter[] = getUserFacingActionParameters(definition, { projectType });
  if (parameters.length === 0) {
    return <span className="kuika-action-picker-popup__preview-muted">No parameters</span>;
  }
  return (
    <ul className="kuika-action-picker-popup__params">
      {parameters.map((entry: IUserFacingActionParameter): JSX.Element => (
        <ParameterRow
          key={entry.parameter.name}
          entry={entry}
          doc={getActionParameterDoc(definition, entry.parameter.name)}
        />
      ))}
    </ul>
  );
};

interface IReturnTypeProps {
  definition: IActionDefinitionDto;
}

const ReturnType = ({ definition }: IReturnTypeProps): JSX.Element => {
  if (isVoidReturnType(definition.returnTypeName)) {
    return <span className="kuika-action-picker-popup__preview-muted">Nothing</span>;
  }
  const iconName: string | undefined = getActionReturnTypeIconName(definition.returnTypeName);
  return (
    <span className="kuika-action-picker-popup__return">
      {iconName ? (
        <KuikaSvg name={iconName} type="action-icons" width={14} height={14} />
      ) : (
        <KuikaSvg name="info-circle" type="common" width={14} height={14} />
      )}
      <span>{definition.returnTypeName}</span>
      {definition.returnCardinality === ECardinality.Many && (
        <span className="kuika-action-picker-popup__chip">list</span>
      )}
    </span>
  );
};

interface IDescriptionProps {
  definition: IActionDefinitionDto;
}

/** Keeps focus in the search field, as the Add button does — the popup's keys are driven from there. */
const keepSearchFocus = (event: React.MouseEvent): void => event.preventDefault();

/**
 * The action's one-line summary, an info button whose tooltip holds the longer explanation, and a link
 * to the action's Academy page. Docs come from the definition's `doc` block; an undocumented action
 * falls back to its (LLM-oriented) description, summarized, with the full text behind the icon.
 */
const Description = ({ definition }: IDescriptionProps): JSX.Element => {
  const summary: string = getActionDocSummary(definition);
  if (!summary) {
    return <p className="kuika-action-picker-popup__preview-muted">No description yet.</p>;
  }
  const details: string | undefined = getActionDocDetails(definition);
  const docUrl: string | undefined = KMainFunctions.isMainAI() ? undefined : getActionDocUrl(definition);
  return (
    <div className="kuika-action-picker-popup__about">
      <div className="kuika-action-picker-popup__description-row">
        <p className="kuika-action-picker-popup__description">{summary}</p>
        {details && (
          <ActionDocTooltip content={details} contentTestId="actionPickerPreviewDetails">
            <KuikaButton
              isIcon
              type="tertiary"
              color="neutral"
              aria-label="More about this action"
              data-testid="actionPickerPreviewInfo"
              className="kuika-action-picker-popup__info"
              onMouseDown={keepSearchFocus}
            >
              <KuikaSvg name="info-circle" type="common" width={14} height={14} />
            </KuikaButton>
          </ActionDocTooltip>
        )}
      </div>
      {docUrl && (
        <a
          className="kuika-action-picker-popup__docs-link"
          href={docUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="actionPickerPreviewDocs"
          onMouseDown={keepSearchFocus}
        >
          <span>Docs</span>
          <KuikaSvg name="external-link" type="common" width={12} height={12} />
        </a>
      )}
    </div>
  );
};

/**
 * The detail pane at the columns' open end: what the pointed-at action does, what it takes and what it
 * returns — read from the action definition the add handler will use, so the pane
 * can never describe something other than what gets added.
 */
export const ActionPickerPreview = ({
  node,
  definition,
  eventLabel,
  lockReason,
  projectType,
  onAdd
}: IActionPickerPreviewProps): JSX.Element => {
  if (!node) {
    return (
      <div
        className="kuika-action-picker-popup__preview kuika-action-picker-popup__preview--idle"
        data-testid="actionPickerPreview"
      >
        <KuikaSvg name="info-circle" type="common" width={20} height={20} />
        <span>Point at an action to see what it does.</span>
      </div>
    );
  }

  const isTrigger: boolean = node.option.category === EActionNodeCategory.triggerEventAction;
  const isLeafEvent: boolean = node.kind === EActionPickerNodeKind.leafEvent;
  const trail: string = getContextTrail(node);
  const addLabel: string = eventLabel ? `Add to ${eventLabel}` : "Add action";

  let body: JSX.Element;
  if (isLeafEvent) {
    body = (
      <p className="kuika-action-picker-popup__description">Adds a block whose actions run again on an interval.</p>
    );
  } else if (isTrigger) {
    const component: string = node.parent?.label ?? "";
    body = (
      <p className="kuika-action-picker-popup__description">
        Runs the actions attached to <strong>{node.label}</strong> of <strong>{component}</strong>.
      </p>
    );
  } else if (definition) {
    body = (
      <>
        <Description definition={definition} />
        {definition.kDatasource_Name && node.isCustom && (
          <PreviewSection title="Source">
            <span className="kuika-action-picker-popup__preview-value">{definition.kDatasource_Name}</span>
          </PreviewSection>
        )}
        <PreviewSection title="Parameters">
          <ParameterList definition={definition} projectType={projectType} />
        </PreviewSection>
        <PreviewSection title="Returns">
          <ReturnType definition={definition} />
        </PreviewSection>
      </>
    );
  } else {
    body = <p className="kuika-action-picker-popup__preview-muted">No details for this action.</p>;
  }

  return (
    <div className="kuika-action-picker-popup__preview" data-testid="actionPickerPreview">
      <div className="kuika-action-picker-popup__preview-scroll">
        <div className="kuika-action-picker-popup__preview-title">
          <span className="kuika-action-picker-popup__preview-name">{definition?.displayName || node.label}</span>
          {trail && <span className="kuika-action-picker-popup__preview-trail">{trail}</span>}
        </div>
        {lockReason && (
          <div className="kuika-action-picker-popup__banner" role="note">
            <KuikaSvg name="lock" type="common" width={14} height={14} />
            <span>{lockReason}</span>
          </div>
        )}
        {body}
      </div>
      <div className="kuika-action-picker-popup__preview-footer">
        <KuikaButton
          type="primary"
          size="sm"
          data-testid="actionPickerPreviewAdd"
          onMouseDown={(event: React.MouseEvent): void => event.preventDefault()}
          onClick={(): void => onAdd(node)}
        >
          {addLabel}
        </KuikaButton>
        <span className="kuika-action-picker-popup__kbd">↵</span>
      </div>
    </div>
  );
};
